import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import EditionStageService from '@/services/http/api/editionStage.ts'
import { editionStageResponseSchema } from '@/schemas/edititionStage'
import { ZodError } from 'zod'
import { EditionStage } from '@/types/backend'
import type { Router } from 'vue-router'
import ROUTE_NAMES from '@/constants/routeNames'

const useEditionStageStore = defineStore('editionStage', () => {
  const editionStage = ref<EditionStage>()

  const fetchEditionStage = async (router: Router): Promise<void> => {
    try {
      const response = await EditionStageService.fetchEditionStage()
      editionStageResponseSchema.parse(response.data)
      const responseValue = response.data.data
      editionStage.value = responseValue.stage as EditionStage
    } catch (error) {
      if (error instanceof ZodError) {
        throw error
      }

      editionStage.value = EditionStage.betweenCampaign
      router.push({ name: ROUTE_NAMES.HOME })
    }
  }

  const isBeforeCampaignSecure = computed<boolean>(
    () => editionStage.value === EditionStage.beforeCampaignSecure
  )

  const isBeforeCampaign = computed<boolean>(
    () =>
      editionStage.value === EditionStage.beforeCampaign ||
      editionStage.value === EditionStage.beforeCampaignSecure
  )

  const isAfterCampaignSecure = computed<boolean>(
    () => editionStage.value === EditionStage.afterCampaignSecure
  )

  const isAfterCampaign = computed<boolean>(
    () =>
      editionStage.value === EditionStage.afterCampaign ||
      editionStage.value === EditionStage.afterCampaignSecure
  )

  const isBetweenCampaign = computed<boolean>(
    () => editionStage.value === EditionStage.betweenCampaign
  )

  const isCampaignUnderway = computed<boolean>(
    () => editionStage.value === EditionStage.campaignUnderway
  )

  const passwordPopup = ref<boolean>(false)

  const setPasswordPopup = (popupState: boolean): void => {
    passwordPopup.value = popupState
  }

  const isPasswordValid = ref<boolean>(false)

  const isSwrOpen = computed<boolean>(
    () =>
      isCampaignUnderway.value ||
      (isBeforeCampaignSecure.value && isPasswordValid.value) ||
      (isAfterCampaignSecure.value && isPasswordValid.value)
  )

  const isSwrClosedInBeforeCampaign = computed<boolean>(
    () => !isSwrOpen.value && isBeforeCampaign.value
  )
  const isSwrClosedInAfterCampaign = computed<boolean>(
    () => !isSwrOpen.value && isAfterCampaign.value
  )
  return {
    editionStage,
    fetchEditionStage,
    passwordPopup,
    setPasswordPopup,
    isBeforeCampaign,
    isBeforeCampaignSecure,
    isAfterCampaign,
    isBetweenCampaign,
    isCampaignUnderway,
    isAfterCampaignSecure,
    isPasswordValid,
    isSwrOpen,
    isSwrClosedInBeforeCampaign,
    isSwrClosedInAfterCampaign
  }
})

export default useEditionStageStore
